import type { MenuProps } from '@material-ui/core/Menu';
import { useCallback, useMemo, useRef, useState } from 'react';

const VIEWPORT_EDGE_PADDING_PX = 16;
const DOWNWARD_GAP_PX = 4;
const UPWARD_LABEL_CLEARANCE_PX = -24;
const MIN_MENU_HEIGHT_PX = 120;

type FlipState = { openUpward: boolean; maxHeight: number };

export function useFlipMenuProps() {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [{ openUpward, maxHeight }, setState] = useState<FlipState>({
        openUpward: false,
        maxHeight: MIN_MENU_HEIGHT_PX,
    });

    const handleOpen = useCallback(() => {
        const el = anchorRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const spaceBelow =
            window.innerHeight - rect.bottom - DOWNWARD_GAP_PX - VIEWPORT_EDGE_PADDING_PX;
        const spaceAbove =
            rect.top - Math.abs(UPWARD_LABEL_CLEARANCE_PX) - VIEWPORT_EDGE_PADDING_PX;
        const nextOpenUpward = spaceAbove > spaceBelow;
        const nextMaxHeight = Math.max(
            nextOpenUpward ? spaceAbove : spaceBelow,
            MIN_MENU_HEIGHT_PX,
        );
        setState({ openUpward: nextOpenUpward, maxHeight: nextMaxHeight });
    }, []);

    const menuProps: Partial<MenuProps> = useMemo(
        () => ({
            classes: { paper: 'fatca-flip-menu-paper' },
            getContentAnchorEl: null,
            anchorOrigin: openUpward
                ? { vertical: 'top', horizontal: 'left' }
                : { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: openUpward
                ? { vertical: 'bottom', horizontal: 'left' }
                : { vertical: 'top', horizontal: 'left' },
            PaperProps: {
                style: {
                    marginTop: openUpward ? UPWARD_LABEL_CLEARANCE_PX : DOWNWARD_GAP_PX,
                    maxHeight,
                },
            },
        }),
        [openUpward, maxHeight],
    );

    return { anchorRef, handleOpen, menuProps };
}

const flipMenuCss = `
.MuiPopover-paper.fatca-flip-menu-paper {
  overflow-y: auto;
  box-shadow:
    0 6px 20px rgba(45, 33, 138, 0.18),
    0 2px 6px rgba(45, 33, 138, 0.10) !important;
}

.fatca-flip-menu-paper .MuiMenuItem-root,
.fatca-flip-menu-paper .MuiListItem-root {
  white-space: normal;
  min-height: 48px;
  height: auto;
  overflow-wrap: anywhere;
  line-height: 1.5;
  padding-top: 8px;
  padding-bottom: 8px;
}
`;

export function FlipMenuStyles() {
    return <style>{flipMenuCss}</style>;
}
